import { posthog, isPostHogEnabled } from "./posthog";
import { getConfiguredApiRootUrl } from "./apiUrl";

const resolveErrorEndpoint = () => {
  const apiRoot = getConfiguredApiRootUrl();
  if (apiRoot) {
    return `${apiRoot}/api/frontend-errors/`;
  }
  return "/api/frontend-errors/";
};

const ERROR_ENDPOINT = resolveErrorEndpoint();
let reportedErrors = new Set<string>();

type ClientErrorKind = "error" | "resource" | "unhandledrejection";

type ClientError = {
  message: string;
  stack?: string;
  url?: string;
  kind?: ClientErrorKind;
};

const UNKNOWN_ERROR_MESSAGE = "Unknown frontend error";

const EXTENSION_PROTOCOL_RE = /(?:chrome|moz|safari)-extension:\/\//i;
const KNOWN_EXTENSION_NOISE_RE =
  /(?:contentScriptMain\.bundle\.js|\[messaging\] Unknown message format|fetchRemoteConfigValues|getRemoteConfigValues|trackAnalyticsEvent|trackError|chrome-extension:\/\/invalid)/i;
const EXTENSION_SENDER_MARKER_RE =
  /(?:"|\\")sender(?:"|\\")\s*:\s*\{(?:"|\\")id(?:"|\\")\s*:/i;

export function isIgnoredBrowserExtensionError(error: ClientError): boolean {
  const text = [error.message, error.stack, error.url]
    .filter(Boolean)
    .join("\n");
  if (EXTENSION_PROTOCOL_RE.test(text)) return true;
  return (
    KNOWN_EXTENSION_NOISE_RE.test(text) && EXTENSION_SENDER_MARKER_RE.test(text)
  );
}

function resolveResourceUrl(target: Element): string {
  const candidate = target as Element & {
    currentSrc?: unknown;
    src?: unknown;
    href?: unknown;
  };
  for (const value of [candidate.currentSrc, candidate.src, candidate.href]) {
    if (typeof value === "string" && value) return value;
  }
  return (
    target.getAttribute("src") ||
    target.getAttribute("href") ||
    target.getAttribute("data") ||
    ""
  );
}

/**
 * Normalize a window "error" event. Runtime errors arrive as ErrorEvent;
 * resource load failures (img/script/link…) are plain Events whose target is
 * the failing element and carry no message/error.
 */
export function describeWindowErrorEvent(event: Event): ClientError {
  if (typeof ErrorEvent !== "undefined" && event instanceof ErrorEvent) {
    const errorMessage =
      typeof event.error?.message === "string" ? event.error.message : "";
    return {
      kind: "error",
      message: event.message || errorMessage || UNKNOWN_ERROR_MESSAGE,
      stack: event.error?.stack,
      url: event.filename || undefined,
    };
  }

  const target = event.target;
  if (typeof Element !== "undefined" && target instanceof Element) {
    const tag = target.tagName.toLowerCase();
    const resourceUrl = resolveResourceUrl(target);
    return {
      kind: "resource",
      message: `Resource failed to load: <${tag}> ${resourceUrl || "(no src)"}`,
      url: resourceUrl || undefined,
    };
  }

  return {
    kind: "error",
    message: `${UNKNOWN_ERROR_MESSAGE} (${event.type || "error"} event)`,
  };
}

function dedupeKey(error: ClientError): string {
  if (error.kind === "resource") {
    return `resource:${error.url || error.message}`;
  }
  return [
    error.kind || "error",
    error.message,
    error.url || "",
    error.stack?.slice(0, 100) || "",
  ].join(":");
}

function reportError(rawError: ClientError) {
  const error: ClientError = {
    ...rawError,
    message:
      (typeof rawError.message === "string" && rawError.message.trim()) ||
      UNKNOWN_ERROR_MESSAGE,
  };
  if (isIgnoredBrowserExtensionError(error)) return;

  // Deduplicate within session
  const key = dedupeKey(error);
  if (reportedErrors.has(key)) return;
  reportedErrors.add(key);

  // Resource failures report the failing asset URL in the message; the page
  // URL is what the backend should log as the location.
  const pageUrl =
    error.kind === "resource"
      ? window.location.href
      : error.url || window.location.href;

  if (isPostHogEnabled()) {
    posthog.captureException(
      Object.assign(new Error(error.message), { stack: error.stack }),
      { url: pageUrl, kind: error.kind || "error" }
    );
  }

  // Don't report in dev
  if (import.meta.env.DEV) return;

  fetch(ERROR_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: error.message,
      stack: error.stack,
      url: pageUrl,
      kind: error.kind || "error",
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    }),
  }).catch(() => {}); // Fire and forget
}

export function initErrorReporting(): () => void {
  const handleError = (event: Event) => {
    const error = describeWindowErrorEvent(event);
    if (isIgnoredBrowserExtensionError(error)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    reportError(error);
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    const error = event.reason;
    const payload: ClientError = {
      kind: "unhandledrejection",
      message: error?.message || String(error),
      stack: error?.stack,
    };
    if (isIgnoredBrowserExtensionError(payload)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    reportError(payload);
  };

  window.addEventListener("error", handleError, true);
  window.addEventListener("unhandledrejection", handleRejection, true);

  return () => {
    window.removeEventListener("error", handleError, true);
    window.removeEventListener("unhandledrejection", handleRejection, true);
  };
}
